import React, { useState } from 'react';
import { PRICING, PRO_FEATURES, TRIAL_DAYS, type BillingInterval } from '../../config/plans';

type Props = {
  /** Called when the user picks a plan. Receives the chosen billing interval. */
  onSelect?: (interval: BillingInterval) => void;
  /** Disable the CTA + show a working state (e.g. while redirecting to Stripe). */
  busy?: boolean;
  /** Heading shown above the card. Hidden when omitted. */
  heading?: string;
  /** Compact layout for use inside a modal (smaller paddings). */
  compact?: boolean;
};

/**
 * Reusable Pro pricing card with a monthly/annual toggle and the Pro feature
 * list. Used both on the landing page and inside the UpgradeModal. Purely
 * presentational — checkout is wired by the parent via `onSelect`.
 */
export const PricingSection: React.FC<Props> = ({ onSelect, busy, heading, compact }) => {
  const [interval, setInterval] = useState<BillingInterval>('year');
  const price = PRICING[interval];

  return (
    <div className={compact ? '' : 'max-w-md mx-auto'}>
      {heading && (
        <h3 className="font-cinzel text-2xl font-bold text-white text-center mb-4">{heading}</h3>
      )}

      {/* Interval toggle */}
      <div className="flex justify-center mb-5">
        <div className="inline-flex bg-black/40 rounded-full p-1 border border-white/10">
          <button
            type="button"
            onClick={() => setInterval('month')}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
              interval === 'month' ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black' : 'text-gray-300 hover:text-white'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setInterval('year')}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 ${
              interval === 'year' ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black' : 'text-gray-300 hover:text-white'
            }`}
          >
            Annual
            <span className="text-[10px] font-bold bg-amber-400 text-gray-900 px-1.5 py-0.5 rounded-full">
              SAVE {PRICING.year.savingsPct}%
            </span>
          </button>
        </div>
      </div>

      <div className="relative rounded-2xl p-[1.5px] bg-gradient-to-b from-amber-400/60 via-amber-500/20 to-transparent shadow-2xl">
        <div className="rounded-2xl bg-gradient-to-b from-green-900/60 to-gray-900 p-6">
          <div className="text-center mb-5">
            <div className="text-sm font-semibold uppercase tracking-widest text-amber-300">Pro</div>
            <div className="mt-1 flex items-end justify-center gap-1">
              <span className="font-cinzel text-5xl font-extrabold text-white">{price.label}</span>
              <span className="text-gray-400 mb-1.5">{price.period}</span>
            </div>
            {interval === 'year' && (
              <div className="text-xs text-gray-400 mt-1">Billed annually</div>
            )}
          </div>

          <ul className="space-y-2.5 mb-6">
            {PRO_FEATURES.map((feature) => (
              <li key={feature} className="flex items-start gap-2.5 text-sm text-gray-100">
                <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            disabled={busy}
            onClick={() => onSelect?.(interval)}
            className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold shadow-lg shadow-amber-500/30 transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {busy ? 'Redirecting…' : TRIAL_DAYS > 0 ? `Start ${TRIAL_DAYS}-day free trial` : 'Upgrade to Pro'}
          </button>
          <p className="text-center text-xs text-gray-400 mt-3">
            {TRIAL_DAYS > 0 ? `${TRIAL_DAYS}-day free trial, then ${price.label}${price.period}. ` : ''}
            Cancel anytime. Secure checkout via Stripe.
          </p>
        </div>
      </div>
    </div>
  );
};
